import { useState, useEffect, useRef } from 'react';
import { Flame, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to build full image URL from relative or absolute path
function getImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
}

// Single condolence card with expand functionality
function CondolenceCard({ condolence }) {
  const [expanded, setExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const textRef = useRef(null);
  const c = condolence;

  // Check if text overflows (needs "read more")
  useEffect(() => {
    if (textRef.current && c.text) {
      const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight) || 20;
      const maxHeight = lineHeight * 3; // 3 lines
      setNeedsExpand(textRef.current.scrollHeight > maxHeight + 5);
    }
  }, [c.text]);

  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-sm p-4 hover:shadow-sm transition-shadow"
      data-testid={`condolence-${c.id}`}
    >
      <div className="flex gap-3">
        {/* Tribute Image */}
        {c.tribute_image && (
          <div className="flex-shrink-0">
            <img
              src={getImageUrl(c.tribute_image.image_url)}
              alt={c.tribute_image.name}
              className="w-12 h-12 object-contain"
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-[#1E293B] text-sm">{c.user_name}</span>
            <span className="text-xs text-[#94A3B8]">
              {c.created_at ? new Date(c.created_at).toLocaleDateString('de-CH') : ''}
            </span>
          </div>

          {/* Content */}
          {c.is_candle_only ? (
            <p className="text-sm text-[#C5A059] italic">Hat eine Kerze angezündet</p>
          ) : (
            <>
              {c.title && (
                <p className="text-sm font-medium text-[#475569] mb-1">{c.title}</p>
              )}
              {c.text && (
                <div>
                  <p 
                    ref={textRef}
                    className={`text-sm text-[#475569] whitespace-pre-line ${expanded ? '' : 'line-clamp-3'}`}
                  >
                    {c.text}
                  </p>
                  {needsExpand && (
                    <button
                      onClick={() => setExpanded(!expanded)}
                      className="mt-2 text-xs text-[#C5A059] hover:text-[#B08D45] flex items-center gap-1 transition-colors"
                      data-testid={`condolence-${c.id}-toggle`}
                    >
                      {expanded ? (
                        <>
                          <ChevronUp className="w-3 h-3" />
                          Weniger anzeigen
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" />
                          Mehr lesen
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CondolenceList({ inseratId, condolencesUrl }) {
  const [condolences, setCondolences] = useState([]);
  const [loading, setLoading] = useState(true);

  const url = condolencesUrl || `${API}/inserate/${inseratId}/condolences`;

  useEffect(() => {
    if (!url) return;

    fetch(url)
      .then(r => r.json())
      .then(data => setCondolences(data.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [url]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (condolences.length === 0) {
    return null; // Don't show section if no condolences
  }

  return (
    <div className="mt-8" data-testid="condolence-list">
      <h3 className="font-heading text-xl text-[#1E293B] mb-4 flex items-center gap-2">
        <Flame className="w-5 h-5 text-[#C5A059]" />
        Kondolenzen ({condolences.length})
      </h3>

      <div className="grid gap-4 md:grid-cols-2">
        {condolences.map(c => (
          <CondolenceCard key={c.id} condolence={c} />
        ))}
      </div>
    </div>
  );
}
